import type { BondOrder } from '../domain/editor.ts';
import type { JSMol, RDKitModule } from '@rdkit/rdkit';
import type { DrawingDocument } from '../model';
import {
  createDrawingDocumentFromChemicalStructure,
  type ChemicalStructureAtom,
  type ChemicalStructureBond
} from '../domain/graphDrawing.ts';
import type { PaperSize } from '../domain/geometry.ts';
import { documentToRdKitInput } from './chemicalValidation.ts';
import { elementSymbol } from './elements.ts';
import { PERIODIC_TABLE } from './periodicTable.ts';
import { loadRdKit } from './rdkitAdapter.ts';

interface RdKitJsonAtom {
  readonly z?: number;
  readonly chg?: number;
  readonly isotope?: number;
  readonly nRad?: number;
  readonly stereo?: string;
}

interface RdKitJsonBond {
  readonly atoms?: readonly number[];
  readonly bo?: number;
  readonly stereo?: string;
  readonly stereoAtoms?: readonly number[];
}

interface RdKitJsonExtension {
  readonly aromaticAtoms?: readonly number[];
  readonly aromaticBonds?: readonly number[];
  readonly cipCodes?: readonly (readonly [number, string])[];
}

interface RdKitJsonMolecule {
  readonly atoms?: readonly RdKitJsonAtom[];
  readonly bonds?: readonly RdKitJsonBond[];
  readonly extensions?: readonly RdKitJsonExtension[];
}

interface RdKitJson {
  readonly defaults?: {
    readonly atom?: RdKitJsonAtom;
    readonly bond?: RdKitJsonBond;
  };
  readonly molecules?: readonly RdKitJsonMolecule[];
}

export interface RawCoordinate {
  readonly x: number;
  readonly y: number;
}

export interface MolBlockGeometry {
  readonly coordinates: readonly RawCoordinate[];
  readonly bondStereo: readonly Readonly<{ fromAtomIndex: number; toAtomIndex: number; code: number }>[];
}

export interface SmilesImportResult {
  readonly document: DrawingDocument;
  readonly canonicalSmiles: string;
}

// #487 (ADR 0011): any real element is a valid molecule candidate, not just EDITOR_ELEMENTS'
// small quick-pick list -- this now accepts everything the periodic-table picker already lets
// you place on the canvas (Z 1-118), not the curated EDIT-tab grid. A rejection here is now only
// ever RDKit returning something outside that range, e.g. z:0 for a wildcard/dummy atom ("*").
const SUPPORTED_ATOMIC_NUMBERS = new Set(PERIODIC_TABLE.map((element) => element.z));

function valueOrDefault<T>(value: T | undefined, fallback: T | undefined): T | undefined {
  return value ?? fallback;
}

export function parseMolBlockGeometry(molBlock: string, expectedAtomCount: number): MolBlockGeometry {
  const lines = molBlock.split(/\r?\n/);
  const countsLineIndex = lines.findIndex((line) => line.includes('V2000'));
  if (countsLineIndex < 0) throw new Error('RDKit did not return a V2000 2D coordinate block.');
  const atomCount = Number.parseInt(lines[countsLineIndex]!.slice(0, 3), 10);
  const bondCount = Number.parseInt(lines[countsLineIndex]!.slice(3, 6), 10);
  if (atomCount !== expectedAtomCount) {
    throw new Error('RDKit returned coordinates for a different number of atoms.');
  }
  const coordinates = lines.slice(countsLineIndex + 1, countsLineIndex + 1 + atomCount).map((line) => ({
    x: Number.parseFloat(line.slice(0, 10)),
    y: Number.parseFloat(line.slice(10, 20))
  }));
  if (coordinates.some((coordinate) => !Number.isFinite(coordinate.x) || !Number.isFinite(coordinate.y))) {
    throw new Error('RDKit returned invalid 2D coordinates.');
  }
  const bondLines = lines.slice(countsLineIndex + 1 + atomCount, countsLineIndex + 1 + atomCount + bondCount);
  return {
    coordinates,
    bondStereo: bondLines.map((line) => ({
      fromAtomIndex: Number.parseInt(line.slice(0, 3), 10) - 1,
      toAtomIndex: Number.parseInt(line.slice(3, 6), 10) - 1,
      code: Number.parseInt(line.slice(9, 12), 10) || 0
    }))
  };
}

function validateImportedAtom(atom: RdKitJsonAtom, defaults: RdKitJsonAtom | undefined): void {
  const atomicNumber = valueOrDefault(atom.z, defaults?.z) ?? 6;
  if (!SUPPORTED_ATOMIC_NUMBERS.has(atomicNumber)) {
    throw new Error(`${elementSymbol(atomicNumber)} is not in the current editor element set.`);
  }
  const isotope = valueOrDefault(atom.isotope, defaults?.isotope) ?? 0;
  if (isotope !== 0 && (!Number.isInteger(isotope) || isotope < 1 || isotope > 300)) {
    throw new Error(`RDKit returned an unsupported isotope mass number ${isotope}.`);
  }
  const radicalElectronCount = valueOrDefault(atom.nRad, defaults?.nRad) ?? 0;
  if (
    radicalElectronCount !== 0 &&
    (!Number.isInteger(radicalElectronCount) || radicalElectronCount < 1 || radicalElectronCount > 3)
  ) {
    throw new Error(`RDKit returned an unsupported radical electron count ${radicalElectronCount}.`);
  }
  if (!['unspecified', 'cw', 'ccw'].includes(valueOrDefault(atom.stereo, defaults?.stereo) ?? 'unspecified')) {
    throw new Error('RDKit returned unsupported atom stereochemistry.');
  }
  const formalCharge = valueOrDefault(atom.chg, defaults?.chg) ?? 0;
  if (!Number.isInteger(formalCharge)) {
    throw new TypeError('RDKit returned a non-integral formal charge.');
  }
}

function validateImportedBond(bond: RdKitJsonBond, defaults: RdKitJsonBond | undefined): void {
  const order = valueOrDefault(bond.bo, defaults?.bo) ?? 1;
  // ADR 0024 (#1127): up to quadruple ('$'), a metal–metal bond.
  if (![1, 2, 3, 4].includes(order)) {
    throw new Error(`Bond order ${order} is not supported; only covalent orders 1 to 4 are available.`);
  }
  if (!['unspecified', 'cis', 'trans'].includes(valueOrDefault(bond.stereo, defaults?.stereo) ?? 'unspecified')) {
    throw new Error('RDKit returned unsupported bond stereochemistry.');
  }
}

function validateSupportedFeatures(json: RdKitJson): RdKitJsonMolecule {
  const molecule = json.molecules?.[0];
  if (!molecule?.atoms?.length) {
    throw new Error('The SMILES does not contain any atoms.');
  }
  if (json.molecules?.length !== 1) {
    throw new Error('SMILES containing multiple RDKit molecule records are not supported.');
  }
  for (const atom of molecule.atoms) {
    validateImportedAtom(atom, json.defaults?.atom);
  }
  for (const bond of molecule.bonds ?? []) {
    validateImportedBond(bond, json.defaults?.bond);
  }
  return molecule;
}

function structureAtoms(
  molecule: RdKitJsonMolecule,
  json: RdKitJson,
  coordinates: readonly RawCoordinate[],
  aromaticAtomIndexes: ReadonlySet<number>,
  cipCodes: ReadonlyMap<number, string>
): readonly ChemicalStructureAtom[] {
  const defaults = json.defaults?.atom;
  return molecule.atoms!.map((atom, index) => {
    const isotope = valueOrDefault(atom.isotope, defaults?.isotope) ?? 0;
    const radicalElectronCount = valueOrDefault(atom.nRad, defaults?.nRad) ?? 0;
    const stereo = valueOrDefault(atom.stereo, defaults?.stereo) ?? 'unspecified';
    let stereoParity: 'clockwise' | 'counterclockwise' | undefined;
    if (stereo === 'cw') stereoParity = 'clockwise';
    else if (stereo === 'ccw') stereoParity = 'counterclockwise';
    return {
      atomicNumber: valueOrDefault(atom.z, defaults?.z) ?? 6,
      formalCharge: valueOrDefault(atom.chg, defaults?.chg) ?? 0,
      x: coordinates[index]!.x,
      y: coordinates[index]!.y,
      aromatic: aromaticAtomIndexes.has(index),
      ...(stereoParity ? { stereoParity } : {}),
      ...(['R', 'S'].includes(cipCodes.get(index) ?? '') ? { stereoDescriptor: cipCodes.get(index) as 'R' | 'S' } : {}),
      ...(isotope !== 0 ? { isotope } : {}),
      ...(radicalElectronCount !== 0 ? { radicalElectronCount } : {})
    };
  });
}

function structureBonds(
  molecule: RdKitJsonMolecule,
  json: RdKitJson,
  atomCount: number,
  aromaticBondIndexes: ReadonlySet<number>,
  molBlockStereo: MolBlockGeometry['bondStereo'],
  stereogenicBondIndexes: ReadonlySet<number>
): readonly ChemicalStructureBond[] {
  const defaults = json.defaults?.bond;
  return (molecule.bonds ?? []).map((bond, index) => {
    const sourceAtomIndex = bond.atoms?.[0];
    const targetAtomIndex = bond.atoms?.[1];
    const order = valueOrDefault(bond.bo, defaults?.bo) ?? 1;
    if (
      !Number.isInteger(sourceAtomIndex) ||
      !Number.isInteger(targetAtomIndex) ||
      sourceAtomIndex! < 0 ||
      sourceAtomIndex! >= atomCount ||
      targetAtomIndex! < 0 ||
      targetAtomIndex! >= atomCount
    ) {
      throw new Error('RDKit returned a bond with invalid atom indexes.');
    }
    const stereo = valueOrDefault(bond.stereo, defaults?.stereo) ?? 'unspecified';
    const wedge = molBlockStereo[index];
    return {
      sourceAtomIndex: sourceAtomIndex!,
      targetAtomIndex: targetAtomIndex!,
      order: order as BondOrder,
      aromatic: aromaticBondIndexes.has(index),
      ...(wedge && [1, 6].includes(wedge.code)
        ? {
            wedgeStyle: wedge.code === 1 ? ('solid' as const) : ('hashed' as const),
            wedgeFromAtomIndex: wedge.fromAtomIndex
          }
        : {}),
      // #1059: a double bond that can be E or Z but whose cis/trans the SMILES leaves open. Drawn
      // plain, its layout would read as one or the other; crossed, it stays open.
      ...(stereogenicBondIndexes.has(index) && order === 2 && !['cis', 'trans'].includes(stereo)
        ? { crossed: true }
        : {}),
      ...(['cis', 'trans'].includes(stereo) && bond.stereoAtoms?.length === 2
        ? {
            stereoConfiguration: stereo as 'cis' | 'trans',
            stereoAtomIndexes: [bond.stereoAtoms[0]!, bond.stereoAtoms[1]!] as const
          }
        : {})
    };
  });
}

/**
 * The bonds that can be E or Z, by index: RDKit marks a double bond "either" (3) when it lays out a
 * structure that gives no cis/trans at all -- with one given, it leaves the open ones unmarked
 * (#1178: `CC=C/C=C/C`), so every bond's cis/trans is taken out first.
 */
function stereogenicDoubleBondIndexes(rdkit: RDKitModule, molecule: RdKitJsonMolecule, json: RdKitJson): Set<number> {
  const bonds = molecule.bonds ?? [];
  const flat: RdKitJson = {
    ...json,
    molecules: [
      {
        ...molecule,
        bonds: bonds.map(({ stereo: _stereo, stereoAtoms: _atoms, ...bond }) => bond),
        extensions: []
      }
    ]
  };
  let layout: JSMol | null = null;
  try {
    layout = rdkit.get_mol(JSON.stringify(flat));
    if (!layout?.is_valid()) return new Set();
    const geometry = parseMolBlockGeometry(layout.get_new_coords(), molecule.atoms?.length ?? 0);
    const indexes = new Set<number>();
    for (const either of geometry.bondStereo.filter((bond) => bond.code === 3)) {
      const index = bonds.findIndex(
        (bond) => bond.atoms?.includes(either.fromAtomIndex) && bond.atoms?.includes(either.toAtomIndex)
      );
      if (index >= 0) indexes.add(index);
    }
    return indexes;
  } catch {
    return new Set();
  } finally {
    layout?.delete();
  }
}

// The shared core of SMILES -> DrawingDocument, without the canonical-SMILES round-trip
// self-check `importSmiles` (below) does afterward. That check exists to catch *user-supplied*
// SMILES containing hydrogen/valence information our layers can't represent -- not relevant to a
// SMILES this codebase generates itself from already-validated Molecule/Isomer data (see
// isomerDepiction.ts), which is why it's factored out rather than duplicated.
export async function buildDocumentFromSmiles(smiles: string, paperSize: PaperSize): Promise<SmilesImportResult> {
  const input = smiles.trim();
  if (!input) throw new Error('Enter a SMILES string to import.');
  const rdkit = await loadRdKit();
  let molecule: JSMol | null = null;
  try {
    molecule = rdkit.get_mol(input);
    if (!molecule?.is_valid()) {
      throw new Error(
        "This isn't a SMILES string PeruseLab can read. Check its brackets, ring numbers, charges and valences."
      );
    }
    const json = JSON.parse(molecule.get_json()) as RdKitJson;
    const parsedMolecule = validateSupportedFeatures(json);
    const aromaticExtension = parsedMolecule.extensions?.find(
      (extension) => (extension.aromaticAtoms?.length ?? 0) > 0 || (extension.aromaticBonds?.length ?? 0) > 0
    );
    const aromaticAtomIndexes = new Set(aromaticExtension?.aromaticAtoms ?? []);
    const aromaticBondIndexes = new Set(aromaticExtension?.aromaticBonds ?? []);
    const geometry = parseMolBlockGeometry(molecule.get_new_coords(), parsedMolecule.atoms!.length);
    const cipCodes = new Map(parsedMolecule.extensions?.flatMap((extension) => extension.cipCodes ?? []) ?? []);
    const atoms = structureAtoms(parsedMolecule, json, geometry.coordinates, aromaticAtomIndexes, cipCodes);
    const bonds = structureBonds(
      parsedMolecule,
      json,
      atoms.length,
      aromaticBondIndexes,
      geometry.bondStereo,
      stereogenicDoubleBondIndexes(rdkit, parsedMolecule, json)
    );
    const document = createDrawingDocumentFromChemicalStructure(paperSize, atoms, bonds);
    return {
      document,
      canonicalSmiles: molecule.get_smiles()
    };
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error('Unable to import this SMILES string.');
  } finally {
    molecule?.delete();
  }
}

export async function importSmiles(smiles: string, paperSize: PaperSize): Promise<SmilesImportResult> {
  const result = await buildDocumentFromSmiles(smiles, paperSize);
  const rdkit = await loadRdKit();
  let roundTripMolecule: JSMol | null = null;
  try {
    roundTripMolecule = rdkit.get_mol(documentToRdKitInput(result.document));
    if (
      !roundTripMolecule ||
      !roundTripMolecule.is_valid() ||
      roundTripMolecule.get_smiles() !== result.canonicalSmiles
    ) {
      throw new Error(
        "PeruseLab can't draw this SMILES exactly as written: it gives hydrogen counts or valences the drawing can't keep."
      );
    }
  } finally {
    roundTripMolecule?.delete();
  }
  return result;
}

/** Whether `text` reads as a SMILES string: one token, which RDKit parses (#1059). */
export async function isSmiles(text: string): Promise<boolean> {
  const input = text.trim();
  if (!input || /\s/.test(input)) return false;
  const rdkit = await loadRdKit();
  let molecule: JSMol | null = null;
  try {
    molecule = rdkit.get_mol(input);
    return Boolean(molecule?.is_valid());
  } catch {
    return false;
  } finally {
    molecule?.delete();
  }
}
